import React from 'react';

const LegalSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="mb-8">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">{title}</h2>
        <div className="space-y-4 text-slate-600 dark:text-zinc-300">{children}</div>
    </div>
);

const PrivacyPolicyPage: React.FC = () => {
    return (
        <div className="py-20 animate-fadeIn">
            <div className="container mx-auto px-4 max-w-4xl">
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white">Privacy Policy</h1>
                    <p className="mt-4 text-lg text-slate-500 dark:text-zinc-400">
                        Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                </div>

                <div className="prose dark:prose-invert prose-lg max-w-none">
                    <LegalSection title="1. Introduction">
                        <p>
                            Welcome to Wampia ("we," "our," or "us"). We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services (collectively, the "Services"). Please read this policy carefully.
                        </p>
                    </LegalSection>

                    <LegalSection title="2. Information We Collect">
                        <p>We may collect information about you in a variety of ways. The information we may collect via the Services includes:</p>
                        <ul>
                            <li><strong>Personal Data:</strong> Personally identifiable information, such as your name, email address, and telephone number, that you voluntarily give to us when you register for the Services or when you choose to participate in various activities related to the Services.</li>
                            <li><strong>Derivative Data:</strong> Information our servers automatically collect when you access the Services, such as your IP address, your browser type, your operating system, your access times, and the pages you have viewed directly before and after accessing the Services.</li>
                            <li><strong>Chat Data:</strong> The content of conversations you have with our AI agents when using our Live Demo or other services. This data is used to improve our AI models and service quality.</li>
                        </ul>
                    </LegalSection>

                    <LegalSection title="3. How We Use Your Information">
                        <p>Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the Services to:</p>
                        <ul>
                            <li>Create and manage your account.</li>
                            <li>Email you regarding your account or order.</li>
                            <li>Respond to customer service requests.</li>
                            <li>Improve our website and service offerings.</li>
                            <li>Monitor and analyze usage and trends to improve your experience with the Services.</li>
                            <li>Prevent fraudulent transactions, monitor against theft, and protect against criminal activity.</li>
                        </ul>
                    </LegalSection>

                    <LegalSection title="4. Disclosure of Your Information">
                        <p>We may share information we have collected about you in certain situations. Your information may be disclosed as follows:</p>
                        <ul>
                            <li><strong>By Law or to Protect Rights:</strong> If we believe the release of information about you is necessary to respond to legal process, to investigate or remedy potential violations of our policies, or to protect the rights, property, and safety of others.</li>
                            <li><strong>Third-Party Service Providers:</strong> We may share your information with third parties that perform services for us or on our behalf, including data analysis, hosting services, and customer service.</li>
                            <li><strong>Business Transfers:</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.</li>
                        </ul>
                    </LegalSection>

                     <LegalSection title="5. Security of Your Information">
                        <p>
                           We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse.
                        </p>
                    </LegalSection>

                    <LegalSection title="6. Your Rights">
                         <p>
                            You have the right to request access to the personal data we hold about you, to request that we correct or delete it, and to object to our processing of your personal data. To make such a request, please use the contact information provided below.
                        </p>
                    </LegalSection>

                    <LegalSection title="7. Changes to This Privacy Policy">
                        <p>
                           We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page. You are advised to review this Privacy Policy periodically for any changes.
                        </p>
                    </LegalSection>

                    <LegalSection title="8. Contact Us">
                        <p>
                            If you have questions or comments about this Privacy Policy, please contact us at: <a href="mailto:privacy@wampia.ai" className="text-[#05F564] hover:underline">privacy@wampia.ai</a>
                        </p>
                    </LegalSection>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicyPage;
